import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const searchMessages = {
  pt: [
    "Conectando com Portal de Inmigración...",
    "Analisando Lei de Extranjería...",
    "Verificando requisitos consulares...",
    "Consultando BOE - Boletín Oficial del Estado...",
    "Processando dados do Ministerio de Inclusión...",
    "Validando procedimentos de Extranjería...",
    "Sincronizando fontes governamentais...",
    "Gerando análise personalizada..."
  ],
  es: [
    "Conectando con Portal de Inmigración...",
    "Analizando Ley de Extranjería...",
    "Verificando requisitos consulares...",
    "Consultando BOE - Boletín Oficial del Estado...",
    "Procesando datos del Ministerio de Inclusión...",
    "Validando procedimientos de Extranjería...",
    "Sincronizando fuentes gubernamentales...",
    "Generando análisis personalizado..."
  ],
  en: [
    "Connecting to Immigration Portal...",
    "Analyzing Immigration Law...",
    "Verifying consular requirements...",
    "Consulting BOE - Official State Gazette...",
    "Processing Ministry of Inclusion data...",
    "Validating immigration procedures...",
    "Synchronizing government sources...",
    "Generating personalized analysis..."
  ]
};

export const JarvisLoader = ({ isLoading, language = 'pt', clientName = '' }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const messages = searchMessages[language] || searchMessages.pt;

  useEffect(() => {
    if (!isLoading) {
      setCurrentMessage(0);
      setProgress(0);
      return;
    }

    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 2000);

    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + Math.random() * 15, 95));
    }, 500);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [isLoading, messages.length]);

  if (!isLoading) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#050608]/98 backdrop-blur-sm flex items-center justify-center"
    >
      <div className="max-w-lg w-full mx-4 text-center">
        
        {/* Brain Animation with rotating rings */}
        <div className="relative w-72 h-72 mx-auto mb-8">
          {/* Outer rotating ring */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0"
          >
            <div className="w-full h-full rounded-full border border-[#C9A24A]/30 border-dashed" />
          </motion.div>

          {/* Middle rotating ring - opposite direction */}
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute inset-4"
          >
            <div className="w-full h-full rounded-full border-2 border-[#00D4FF]/20" />
          </motion.div>

          {/* Inner rotating ring */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            className="absolute inset-8"
          >
            <div className="w-full h-full rounded-full border border-[#C9A24A]/50" />
          </motion.div>

          {/* Brain Image - pulsing */}
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              opacity: [0.9, 1, 0.9]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-12 flex items-center justify-center"
          >
            <img 
              src="/assets/brain-ai.png" 
              alt="AI Engine"
              className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(0,212,255,0.5)]"
            />
          </motion.div>

          {/* Glow effect */}
          <div 
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(0,212,255,0.1) 0%, rgba(201,162,74,0.05) 50%, transparent 70%)'
            }}
          />

          {/* Orbiting particles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-[#C9A24A] rounded-full"
              style={{
                top: '50%',
                left: '50%',
              }}
              animate={{
                x: [0, Math.cos(i * 60 * Math.PI / 180) * 130, 0],
                y: [0, Math.sin(i * 60 * Math.PI / 180) * 130, 0],
                opacity: [0.3, 1, 0.3],
                scale: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>

        {/* Spain Flag */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <img 
            src="https://flagcdn.com/w40/es.png" 
            alt="España" 
            className="w-10 h-auto rounded shadow-lg"
          />
          <span className="text-[#C9A24A] text-sm font-medium tracking-wider">
            MOTOR DE INTELIGÊNCIA JURÍDICA
          </span>
        </div>

        {/* Loading text */}
        <motion.div
          key={currentMessage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="h-8 mb-4"
        >
          <p className="text-[#00D4FF] font-mono text-sm tracking-wider">
            {messages[currentMessage]}
          </p>
        </motion.div>

        {/* Progress bar */}
        <div className="w-full max-w-xs mx-auto mb-6">
          <div className="h-1 bg-[#C9A24A]/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#C9A24A] to-[#00D4FF]"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <p className="text-[#C9A24A]/60 text-xs mt-2 font-mono">{Math.round(progress)}%</p>
        </div>

        {/* Client name greeting */}
        {clientName && (
          <p className="text-white/60 text-sm">
            {language === 'pt' ? `Preparando análise para ${clientName}...` :
             language === 'es' ? `Preparando análisis para ${clientName}...` :
             `Preparing analysis for ${clientName}...`}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default JarvisLoader;
